import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { loadPlatformFavicon } from "./lib/favicon";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { DuplicateGuardProvider } from "./context/DuplicateGuard";
import { TargaGuardProvider } from "./context/TargaGuard";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import DemoRegister from "./pages/DemoRegister";
import SegnalatoreLogin from "./pages/segnalatore/SegnalatoreLogin";
import SegnalatoreArea from "./pages/segnalatore/SegnalatoreArea";
import { SegnalatoriPwa, ClientePwa } from "./components/SegnalatoriPwa";
import AuthCallback from "./pages/AuthCallback";
import Placeholder from "./pages/Placeholder";
import { useDisabledModules } from "./lib/moduli";

import AdminDashboard from "./pages/superadmin/AdminDashboard";
import Studi from "./pages/superadmin/Studi";
import StorageConfig from "./pages/superadmin/StorageConfig";
import Branding from "./pages/superadmin/Branding";
import EmailSettings from "./pages/superadmin/EmailSettings";
import RemoteSupport from "./pages/superadmin/RemoteSupport";
import SitoWeb from "./pages/superadmin/SitoWeb";
import ImpostazioniAllegati from "./pages/superadmin/ImpostazioniAllegati";
import RootPage from "./pages/RootPage";
import Importazione from "./pages/superadmin/Importazione";
import Manutenzione from "./pages/superadmin/Manutenzione";
import Gdpr from "./pages/superadmin/Gdpr";
import StudioPrivacy from "./pages/studio/StudioPrivacy";
import RichiesteRinnovo from "./pages/superadmin/RichiesteRinnovo";
import CompagnieMaster from "./pages/superadmin/CompagnieMaster";
import RichiesteCompagnie from "./pages/superadmin/RichiesteCompagnie";
import StudioDashboard from "./pages/studio/StudioDashboard";
import Operatori from "./pages/studio/Operatori";
import Clienti from "./pages/studio/Clienti";
import Impostazioni from "./pages/studio/Impostazioni";
import BookmarkletAruba from "./pages/studio/BookmarkletAruba";
import Pratiche from "./pages/studio/Pratiche";
import ArchivioPratiche from "./pages/studio/ArchivioPratiche";
import Agenda from "./pages/studio/Agenda";
import Anagrafiche from "./pages/studio/Anagrafiche";
import Contabilita from "./pages/studio/Contabilita";
import FattureProforma from "./pages/studio/FattureProforma";
import RiepilogoSegnalatori from "./pages/studio/RiepilogoSegnalatori";
import MessaggiSegnalatori from "./pages/studio/MessaggiSegnalatori";
import Lettere from "./pages/studio/Lettere";
import MailClient from "./pages/studio/MailClient";
import Messaggi from "./pages/studio/Messaggi";
import Rubrica from "./pages/studio/Rubrica";
import AllegatiStudio from "./pages/studio/AllegatiStudio";
import ImportExport from "./pages/studio/ImportExport";
import Cestino from "./pages/studio/Cestino";
import ClienteLogin from "./pages/clientearea/ClienteLogin";
import ClienteAccesso from "./pages/clientearea/ClienteAccesso";
import ClientePortal from "./pages/clientearea/ClientePortal";
import FotoPage from "./pages/foto/FotoPage";
import CondivisionePublic from "./pages/CondivisionePublic";
import FirmaPublic from "./pages/FirmaPublic";
import ProLogin from "./pages/professionisti/ProLogin";
import ProArea from "./pages/professionisti/ProArea";


function ModGuard({ k, children }) {
  const { user } = useAuth();
  const off = useDisabledModules(!!user?.tenant_id && user.role !== "superadmin");
  if (off.has(k)) {
    return <Placeholder title="Modulo non attivo" subtitle="Questa sezione non è attiva per il tuo studio. Contatta l'amministrazione PatroGestPro." icon="Lock" testId="module-disabled" />;
  }
  return children;
}

function SectionGuard({ section, children }) {
  const { can } = useAuth();
  if (!can(section)) {
    return (
      <Placeholder
        title="Accesso non consentito"
        subtitle="Non disponi dei permessi per questa sezione. Contatta il titolare dello studio."
        icon="Lock"
        testId="access-denied"
      />
    );
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Callback OAuth: session_id arriva nel fragment dell'URL.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/demo/:codice" element={<DemoRegister />} />
      <Route path="/foto" element={<FotoPage />} />
      <Route path="/cliente" element={<><ClientePortal /><ClientePwa /></>} />
      <Route path="/condivisione/:token" element={<CondivisionePublic />} />
      <Route path="/firma/:token" element={<FirmaPublic />} />
      <Route path="/professionisti" element={<ProLogin />} />
      <Route path="/professionisti/area" element={<ProArea />} />
      <Route path="/cliente/login" element={<><ClienteLogin /><ClientePwa /></>} />
      <Route path="/cliente/accesso" element={<><ClienteAccesso /><ClientePwa /></>} />
      <Route path="/area/*" element={<Navigate to="/cliente" replace />} />
      <Route path="/segnalatori" element={<><SegnalatoreLogin /><SegnalatoriPwa /></>} />
      <Route path="/segnalatori/area" element={<><SegnalatoreArea /><SegnalatoriPwa /></>} />

      <Route path="/" element={<RootPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<Layout />}>

          {/* Superadmin */}
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/studi" element={<Studi />} />
          <Route path="/admin/storage" element={<StorageConfig />} />
          <Route path="/admin/branding" element={<Branding />} />
          <Route path="/admin/email" element={<EmailSettings />} />
          <Route path="/admin/assistenza-remota" element={<RemoteSupport />} />
          <Route path="/admin/sito-web" element={<SitoWeb />} />
          <Route path="/admin/impostazioni-allegati" element={<ImpostazioniAllegati />} />
          <Route path="/admin/importazione" element={<Importazione />} />
          <Route path="/admin/manutenzione" element={<Manutenzione />} />
          <Route path="/admin/gdpr" element={<Gdpr />} />
          <Route path="/admin/richieste-rinnovo" element={<RichiesteRinnovo />} />
          <Route path="/admin/compagnie-master" element={<CompagnieMaster />} />
          <Route path="/admin/richieste-compagnie" element={<RichiesteCompagnie />} />

          {/* Studio */}
          <Route path="/app/dashboard" element={<StudioDashboard />} />
          <Route path="/app/agenda" element={<ModGuard k="agenda"><Agenda /></ModGuard>} />
          <Route path="/app/pratiche" element={<SectionGuard section="pratiche"><Pratiche /></SectionGuard>} />
          <Route path="/app/archivio" element={<ModGuard k="archivio"><SectionGuard section="pratiche"><ArchivioPratiche /></SectionGuard></ModGuard>} />
          <Route path="/app/scadenzario" element={<SectionGuard section="scadenzario"><Placeholder title="Scadenzario" subtitle="Scadenze, promemoria e termini di legge." icon="CalendarClock" testId="scadenzario-page" features={["Scadenze per pratica", "Promemoria automatici", "Vista calendario", "Termini prescrizione"]} /></SectionGuard>} />
          <Route path="/app/clienti" element={<SectionGuard section="clienti"><Clienti /></SectionGuard>} />
          <Route path="/app/anagrafiche" element={<SectionGuard section="anagrafiche"><Anagrafiche /></SectionGuard>} />
          <Route path="/app/lettere" element={<ModGuard k="lettere"><SectionGuard section="lettere"><Lettere /></SectionGuard></ModGuard>} />
          <Route path="/app/allegati-studio" element={<ModGuard k="allegati-studio"><SectionGuard section="allegati_studio"><AllegatiStudio /></SectionGuard></ModGuard>} />
          <Route path="/app/email" element={<ModGuard k="email"><MailClient account="email" /></ModGuard>} />
          <Route path="/app/pec" element={<ModGuard k="pec"><MailClient account="pec" /></ModGuard>} />
          <Route path="/app/messaggi" element={<ModGuard k="messaggi"><SectionGuard section="messaggi"><Messaggi /></SectionGuard></ModGuard>} />
          <Route path="/app/messaggi-segnalatori" element={<SectionGuard section="messaggi"><MessaggiSegnalatori /></SectionGuard>} />
          <Route path="/app/rubrica" element={<ModGuard k="rubrica"><SectionGuard section="rubrica"><Rubrica /></SectionGuard></ModGuard>} />
          <Route path="/app/documenti" element={<SectionGuard section="documenti"><Placeholder title="Documenti" subtitle="Archivio documenti e perizie per pratica." icon="FileArchive" testId="documenti-page" features={["Upload documenti", "Organizzazione per pratica", "Perizie e allegati", "Condivisione con cliente"]} /></SectionGuard>} />
          <Route path="/app/contabilita" element={<ModGuard k="contabilita"><SectionGuard section="contabilita"><Contabilita /></SectionGuard></ModGuard>} />
          <Route path="/app/riepilogo-segnalatori" element={<ModGuard k="riepilogo-segnalatori"><SectionGuard section="contabilita"><RiepilogoSegnalatori /></SectionGuard></ModGuard>} />
          <Route path="/app/proforme" element={<ModGuard k="proforme"><SectionGuard section="contabilita"><FattureProforma /></SectionGuard></ModGuard>} />
          <Route path="/app/operatori" element={<Operatori />} />
          <Route path="/app/impexp" element={<ImportExport />} />
          <Route path="/app/cestino" element={<Cestino />} />
          <Route path="/app/privacy" element={<StudioPrivacy />} />
          <Route path="/app/impostazioni" element={<Impostazioni />} />
          <Route path="/app/bookmarklet-aruba" element={<BookmarkletAruba />} />

          {/* Cliente: area spostata su /cliente */}
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => { loadPlatformFavicon(); }, []);
  return (
    <div className="App notranslate" translate="no">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <DuplicateGuardProvider>
              <TargaGuardProvider>
                <AppRoutes />
              </TargaGuardProvider>
            </DuplicateGuardProvider>
          </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
